import {
  Box,
  Button,
  Grid,
  HStack,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Image } from "../reusables";
import { BiPlay } from "react-icons/bi";
import { TbThumbUp, TbThumbUpFilled } from "react-icons/tb";
import { useNavigate } from "react-router";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import useColorAccent from "../../hooks/useColorAccent";
import { Link } from "react-router";
import useTimeout from "../../hooks/useTimeout";
import dayjs from "../../helpers/dayJs";
import { FiClock } from "react-icons/fi";
import useImageCompressor from "../../hooks/useImageCompressor";
import User from "../user";
import { useHlsAudioActions } from "../../wrappers/hlsAudio";
import useReactions from "../../hooks/data/useReactions";
import useReactiveQuery from "../../hooks/data/useReactiveQuery";

/**
 *
 * @param {import("@chakra-ui/react").StackProps & {id: string, queryId?: string}} props
 *
 */
export default function RegularPost(props) {
  const [isMobile] = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();
  const { id: stateUserId } = useUserStore((state) => state.user, shallow);
  const { attachImage, accentColor, counterColor } = useColorAccent();
  const { playTrackPreview, stopTrackPreview, previewTrack } =
    useHlsAudioActions();
  const { reset, clear } = useTimeout(
    () => playTrackPreview(post?.user?.id, post?.id, post),
    500,
    false
  );
  const dataContainerRef = useRef();
  const imageContainerRef = useRef();
  const { findClosestMatch } = useImageCompressor();
  const { get, getAll } = useReactiveQuery();
  const { id, queryId, ...stackProps } = props;
  const post = get(["posts", id]);

  const {
    addLikeReaction,
    removeLikeReaction,
    addDislikeReaction,
    removeDislikeReaction,
  } = useReactions({
    type: "post",
    contentId: post?.id,
    contentUserId: post?.user?.id,
  });

  async function updateLikeReaction() {
    if (post.isLikedByUser) {
      removeLikeReaction();
    } else {
      addLikeReaction();
    }
  }

  function stopPreview() {
    clear();
    stopTrackPreview();
  }

  function handleMouseEnter(event) {
    if (dataContainerRef.current) {
      const isOverDataContainer = dataContainerRef.current.contains(
        event.target
      );
      if (
        !isOverDataContainer &&
        !previewTrack?.isPlaying &&
        !previewTrack?.isLoading &&
        post?.status?.engaging_part_extracted
      ) {
        reset();
      } else if (isOverDataContainer) {
        stopTrackPreview();
        clear();
      }
    }
  }

  useEffect(() => {
    return () => {
      stopTrackPreview();
      clear();
    };
  }, []);

  const formatDate = (date) => {
    if (!date) return "";

    const now = dayjs();
    const postDate = dayjs(date);
    const diffInDays = now.diff(postDate, "days", true);
    const diffInWeeks = now.diff(postDate, "weeks", true);

    // Today
    if (postDate.isSame(now, "day")) {
      return "Today";
    }

    // Yesterday
    if (postDate.isSame(now.clone().subtract(1, "day"), "day")) {
      return "Yesterday";
    }

    // This week (older than yesterday but less than 7 days)
    if (diffInDays >= 2 && diffInDays < 7) {
      return "This week";
    }

    // A week ago (between 7 days and 1.5 weeks)
    if (diffInWeeks >= 1 && diffInWeeks < 1.5) {
      return "A week ago";
    }

    // Older than 1.5 weeks - show date in DD/MM/YYYY format
    return postDate.format("DD/MM/YYYY");
  };

  const formattedDate =
    post?.createdAt && post?.createdAt?.toDate
      ? formatDate(post?.createdAt.toDate())
      : "";

  const thumbnailFileName = findClosestMatch(
    post?.track?.thumbnails,
    imageContainerRef
  )?.fileName;

  return (
    <Grid
      gridTemplateRows={"max-content auto"}
      borderRadius={"xl"}
      gridRowGap={"2"}
      role="group"
      cursor={"pointer"}
      as={Link}
      to={`/user/${post?.user?.username}/post/${post.id}`}
      state={{ autoPlay: true, queryId: queryId }}
      _hover={{
        textDecor: "none",
      }}
      {...stackProps}
    >
      <Box pos="relative" h="max-content">
        <Image
          ref={imageContainerRef}
          firebaseStoragePath={
            thumbnailFileName
              ? `photos/${post?.user?.id}/${post?.id}/thumbnails/${thumbnailFileName}`
              : null
          }
          borderRadius={"xl"}
          // h="full"
          h="auto"
          w="full"
          aspectRatio={"4 / 2.5"}
          onLoad={attachImage}
        />
        <Text
          transition={"all 150ms"}
          fontSize={"12px"}
          display={"flex"}
          px="1"
          py="0.5"
          gap="2"
          alignItems={"center"}
          bg={`${accentColor}70`}
          _groupHover={{
            bg: "black",
            color: "white",
          }}
          backdropFilter={"blur(5px)"}
          borderRadius={"md"}
          color={counterColor}
          fontWeight={"semibold"}
          pos="absolute"
          bottom={"2"}
          right={"1.5"}
        >
          <FiClock />
          {dayjs.utc(post.duration * 1000).format("mm:ss")}
        </Text>
      </Box>
      <VStack
        transition={"all 150ms"}
        w="full"
        align={"flex-start"}
        spacing={"0.5"}
        px="2"
        opacity={"0.75"}
        _groupHover={{
          opacity: 1,
        }}
      >
        <Text fontSize={"md"} fontWeight={"bold"} noOfLines={"2"}>
          {post?.title}
        </Text>
        {stateUserId == post?.user?.id ? (
          <Text fontSize={"xs"}>{formattedDate}</Text>
        ) : (
          <User.Container user={post?.user}>
            <HStack justify={"space-between"}>
              <User.Name
                variant="username"
                fontSize={"xs"}
                fontWeight={"semibold"}
                color={"gray.400"}
                spacing={"1"}
              >
                <Text fontSize={"xs"}>
                  {"  - "}
                  {formattedDate}
                </Text>
              </User.Name>
            </HStack>
          </User.Container>
        )}
        {post?.streamCount > 0 && (
          <HStack fontSize={"xs"} spacing={"4"} pt="1">
            <HStack spacing={"1"}>
              <BiPlay size={18} />
              <Text>{post?.streamCount}</Text>
            </HStack>
            <HStack spacing={"1"}>
              <TbThumbUp size={14} />
              <Text>{post?.likeCount}</Text>
            </HStack>
          </HStack>
        )}
      </VStack>
    </Grid>
  );
}
